/**
 * Referral Service
 *
 * Handles referral program functionality including:
 * - Generating unique referral codes
 * - Creating and tracking referrals
 * - Processing referral rewards
 * - Referral statistics
 */

import { supabase } from './supabaseService';
import {
  Referral,
  ReferralProgramSettings,
  ReferralStatistics
} from '../types/multiTenant';

// =============================================================================
// CONSTANTS
// =============================================================================

const REFERRAL_CODE_LENGTH = 8;
const DEFAULT_EXPIRATION_DAYS = 30;
const BASE_URL = window.location.origin;

// =============================================================================
// REFERRAL CODE GENERATION
// =============================================================================

/**
 * Generate a unique referral code
 */
const generateReferralCode = (orgName: string): string => {
  // Create base from org name (first 3 chars uppercase)
  const base = orgName
    .replace(/[^a-zA-Z0-9]/g, '')
    .substring(0, 3)
    .toUpperCase();

  // Generate random suffix
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let suffix = '';
  for (let i = 0; i < REFERRAL_CODE_LENGTH - base.length; i++) {
    suffix += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  return `${base}${suffix}`;
};

/**
 * Generate referral link from code
 */
const generateReferralLink = (code: string): string => {
  // Inclui trial=true para que o usuário indicado caia direto no trial grátis
  // (e não no fluxo pago padrão). Backstop: OrganizationSignup já trata
  // ausência de ?plan=X como trial, mas explicitar evita ambiguidade no UTM.
  return `${BASE_URL}/signup?trial=true&ref=${code}`;
};

// =============================================================================
// PROGRAM SETTINGS
// =============================================================================

/**
 * Get referral program settings for an organization
 */
export const getReferralSettings = async (
  organizationId: string
): Promise<ReferralProgramSettings | null> => {
  try {
    const { data, error } = await supabase
      .from('referral_program_settings')
      .select('*')
      .eq('organization_id', organizationId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No settings found, return default
        return null;
      }
      throw error;
    }

    return mapDbToSettings(data);
  } catch (error) {
    console.error('Error fetching referral settings:', error);
    return null;
  }
};

/**
 * Create or update referral program settings
 */
export const saveReferralSettings = async (
  settings: Partial<ReferralProgramSettings> & { organizationId: string }
): Promise<boolean> => {
  try {
    const dbData = mapSettingsToDb(settings);

    const { error } = await supabase
      .from('referral_program_settings')
      .upsert(dbData, { onConflict: 'organization_id' });

    if (error) throw error;

    return true;
  } catch (error) {
    console.error('Error saving referral settings:', error);
    return false;
  }
};

// =============================================================================
// REFERRAL OPERATIONS
// =============================================================================

/**
 * Get or create the organization's referral code
 */
export const getOrCreateReferralCode = async (
  organizationId: string,
  organizationName: string
): Promise<{ code: string; link: string } | null> => {
  try {
    // Check if code already exists
    const { data: existing, error: selectError } = await supabase
      .from('organization_referral_codes')
      .select('code')
      .eq('organization_id', organizationId)
      .single();

    // If table doesn't exist, generate a local code (won't be persisted)
    if (selectError && (selectError.code === '42P01' || selectError.code === 'PGRST204' || selectError.message?.includes('relation'))) {
      const localCode = generateReferralCode(organizationName);
      return {
        code: localCode,
        link: generateReferralLink(localCode)
      };
    }

    if (existing) {
      return {
        code: existing.code,
        link: generateReferralLink(existing.code)
      };
    }

    // Generate new code
    let code = generateReferralCode(organizationName);
    let attempts = 0;

    // Ensure uniqueness
    while (attempts < 10) {
      const { data: exists, error: checkError } = await supabase
        .from('organization_referral_codes')
        .select('code')
        .eq('code', code)
        .single();

      // If table doesn't exist, just use the generated code
      if (checkError && (checkError.code === '42P01' || checkError.code === 'PGRST204')) {
        break;
      }

      if (!exists) break;

      code = generateReferralCode(organizationName);
      attempts++;
    }

    // Save the code
    const { error } = await supabase
      .from('organization_referral_codes')
      .insert({
        organization_id: organizationId,
        code,
        created_at: new Date().toISOString()
      });

    // If table doesn't exist, still return the code (just won't be persisted)
    if (error && (error.code === '42P01' || error.code === 'PGRST204' || error.message?.includes('relation'))) {
      return {
        code,
        link: generateReferralLink(code)
      };
    }

    if (error) throw error;

    return {
      code,
      link: generateReferralLink(code)
    };
  } catch (error: any) {
    // Silently handle table not found errors
    if (error?.code === '42P01' || error?.code === 'PGRST204' || error?.message?.includes('relation')) {
      const localCode = generateReferralCode(organizationName);
      return {
        code: localCode,
        link: generateReferralLink(localCode)
      };
    }
    console.error('Error getting/creating referral code:', error);
    return null;
  }
};

/**
 * Create a new referral invitation
 */
export const createReferralInvite = async (
  organizationId: string,
  referralCode: string,
  email?: string,
  source: 'email' | 'whatsapp' | 'social' | 'direct' = 'direct'
): Promise<Referral | null> => {
  try {
    const now = new Date();
    const expiresAt = new Date(now);
    expiresAt.setDate(expiresAt.getDate() + DEFAULT_EXPIRATION_DAYS);

    const referralData = {
      referrer_organization_id: organizationId,
      referred_email: email || null,
      referral_code: referralCode,
      referral_link: generateReferralLink(referralCode),
      status: 'pending',
      reward_type: 'free_month',
      reward_value: 1,
      reward_applied: false,
      source,
      created_at: now.toISOString(),
      updated_at: now.toISOString(),
      expires_at: expiresAt.toISOString()
    };

    const { data, error } = await supabase
      .from('referrals')
      .insert(referralData)
      .select()
      .single();

    if (error) throw error;

    return mapDbToReferral(data);
  } catch (error) {
    console.error('Error creating referral invite:', error);
    return null;
  }
};

/**
 * Get all referrals for an organization
 */
export const getReferrals = async (
  organizationId: string,
  status?: Referral['status']
): Promise<Referral[]> => {
  try {
    let query = supabase
      .from('referrals')
      .select('*')
      .eq('referrer_organization_id', organizationId)
      .order('created_at', { ascending: false });

    if (status) {
      query = query.eq('status', status);
    }

    const { data, error } = await query;

    if (error) throw error;

    return (data || []).map(mapDbToReferral);
  } catch (error) {
    console.error('Error fetching referrals:', error);
    return [];
  }
};

/**
 * Process a referral signup (when someone signs up using a referral code)
 */
export const processReferralSignup = async (
  referralCode: string,
  newOrganizationId: string
): Promise<boolean> => {
  try {
    // Find the referral by code
    const { data: referral, error: fetchError } = await supabase
      .from('referrals')
      .select('*')
      .eq('referral_code', referralCode)
      .eq('status', 'pending')
      .single();

    if (fetchError || !referral) {
      // Try to find by organization code
      const { data: orgCode } = await supabase
        .from('organization_referral_codes')
        .select('organization_id')
        .eq('code', referralCode)
        .single();

      if (!orgCode) {
        console.warn('Referral code not found:', referralCode);
        return false;
      }

      // Create a referral record for tracking
      await supabase.from('referrals').insert({
        referrer_organization_id: orgCode.organization_id,
        referred_organization_id: newOrganizationId,
        referral_code: referralCode,
        referral_link: generateReferralLink(referralCode),
        status: 'signed_up',
        reward_type: 'free_month',
        reward_value: 1,
        reward_applied: false,
        source: 'direct',
        signed_up_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });

      return true;
    }

    // Update existing referral
    const { error: updateError } = await supabase
      .from('referrals')
      .update({
        referred_organization_id: newOrganizationId,
        status: 'signed_up',
        signed_up_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', referral.id);

    if (updateError) throw updateError;

    return true;
  } catch (error) {
    console.error('Error processing referral signup:', error);
    return false;
  }
};

/**
 * Process referral conversion (when referred org makes first payment)
 */
export const processReferralConversion = async (
  referredOrganizationId: string
): Promise<boolean> => {
  try {
    const { data: referral, error: fetchError } = await supabase
      .from('referrals')
      .select('*')
      .eq('referred_organization_id', referredOrganizationId)
      .eq('status', 'signed_up')
      .single();

    if (fetchError || !referral) {
      return false;
    }

    const { error: updateError } = await supabase
      .from('referrals')
      .update({
        status: 'converted',
        converted_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', referral.id);

    if (updateError) throw updateError;

    // Trigger reward processing
    await processReferralReward(referral.id);

    return true;
  } catch (error) {
    console.error('Error processing referral conversion:', error);
    return false;
  }
};

/**
 * Apply reward to referrer organization
 */
export const processReferralReward = async (
  referralId: string
): Promise<boolean> => {
  try {
    const { data: referral, error: fetchError } = await supabase
      .from('referrals')
      .select('*')
      .eq('id', referralId)
      .single();

    if (fetchError || !referral) {
      return false;
    }

    // For now, just mark as rewarded - actual reward logic depends on billing system
    // In production, this would extend subscription or add credit
    const { error: updateError } = await supabase
      .from('referrals')
      .update({
        status: 'rewarded',
        reward_applied: true,
        rewarded_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', referralId);

    if (updateError) throw updateError;

    // Update settings stats
    await supabase.rpc('increment_referral_stats', {
      org_id: referral.referrer_organization_id
    });

    return true;
  } catch (error) {
    console.error('Error processing referral reward:', error);
    return false;
  }
};

// =============================================================================
// STATISTICS
// =============================================================================

/**
 * Get referral statistics for an organization
 */
export const getReferralStatistics = async (
  organizationId: string
): Promise<ReferralStatistics> => {
  try {
    const { data: referrals } = await supabase
      .from('referrals')
      .select('status, created_at')
      .eq('referrer_organization_id', organizationId);

    const now = new Date();
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    const stats: ReferralStatistics = {
      totalReferralsSent: referrals?.length || 0,
      pendingReferrals: 0,
      signedUpReferrals: 0,
      convertedReferrals: 0,
      rewardedReferrals: 0,
      totalRewardsEarned: 0,
      conversionRate: 0,
      thisMonthReferrals: 0,
      thisMonthConversions: 0
    };

    if (referrals) {
      for (const r of referrals) {
        const createdDate = new Date(r.created_at);

        switch (r.status) {
          case 'pending':
            stats.pendingReferrals++;
            break;
          case 'signed_up':
            stats.signedUpReferrals++;
            break;
          case 'converted':
            stats.convertedReferrals++;
            break;
          case 'rewarded':
            stats.rewardedReferrals++;
            stats.totalRewardsEarned++;
            break;
        }

        if (createdDate >= thisMonth) {
          stats.thisMonthReferrals++;
          if (r.status === 'converted' || r.status === 'rewarded') {
            stats.thisMonthConversions++;
          }
        }
      }

      // Calculate conversion rate
      const totalWithSignup = stats.signedUpReferrals + stats.convertedReferrals + stats.rewardedReferrals;
      if (stats.totalReferralsSent > 0) {
        stats.conversionRate = (totalWithSignup / stats.totalReferralsSent) * 100;
      }
    }

    return stats;
  } catch (error) {
    console.error('Error fetching referral statistics:', error);
    return {
      totalReferralsSent: 0,
      pendingReferrals: 0,
      signedUpReferrals: 0,
      convertedReferrals: 0,
      rewardedReferrals: 0,
      totalRewardsEarned: 0,
      conversionRate: 0,
      thisMonthReferrals: 0,
      thisMonthConversions: 0
    };
  }
};

// =============================================================================
// SHARE FUNCTIONALITY
// =============================================================================

/**
 * Generate share message for referral
 */
export const generateShareMessage = (
  referralLink: string,
  organizationName: string,
  customMessage?: string
): string => {
  if (customMessage) {
    return customMessage.replace('{{link}}', referralLink);
  }

  return `Olá! Gostaria de te indicar o PsicolSys, o sistema que uso para gerenciar minha clínica.

Use meu link de indicação e ganhe 1 mês grátis:
${referralLink}

- ${organizationName}`;
};

/**
 * Copy referral link to clipboard
 */
export const copyReferralLink = async (link: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(link);
    return true;
  } catch (error) {
    console.error('Error copying to clipboard:', error);
    return false;
  }
};

/**
 * Share via Web Share API (mobile)
 */
export const shareReferral = async (
  link: string,
  organizationName: string
): Promise<boolean> => {
  if (!navigator.share) {
    return false;
  }

  try {
    await navigator.share({
      title: 'Indicação PsicolSys',
      text: generateShareMessage(link, organizationName),
      url: link
    });
    return true;
  } catch (error) {
    if ((error as Error).name !== 'AbortError') {
      console.error('Error sharing:', error);
    }
    return false;
  }
};

// =============================================================================
// DATABASE MAPPING HELPERS
// =============================================================================

const mapDbToReferral = (data: any): Referral => ({
  id: data.id,
  referrerOrganizationId: data.referrer_organization_id,
  referredOrganizationId: data.referred_organization_id,
  referredEmail: data.referred_email,
  referralCode: data.referral_code,
  referralLink: data.referral_link,
  status: data.status,
  signedUpAt: data.signed_up_at,
  convertedAt: data.converted_at,
  rewardedAt: data.rewarded_at,
  rewardType: data.reward_type,
  rewardValue: data.reward_value,
  rewardApplied: data.reward_applied,
  rewardExpiresAt: data.reward_expires_at,
  campaignId: data.campaign_id,
  source: data.source,
  metadata: data.metadata,
  createdAt: data.created_at,
  updatedAt: data.updated_at,
  expiresAt: data.expires_at
});

const mapDbToSettings = (data: any): ReferralProgramSettings => ({
  id: data.id,
  organizationId: data.organization_id,
  isEnabled: data.is_enabled,
  referrerRewardType: data.referrer_reward_type,
  referrerRewardValue: data.referrer_reward_value,
  referredRewardType: data.referred_reward_type,
  referredRewardValue: data.referred_reward_value,
  maxReferralsPerMonth: data.max_referrals_per_month,
  maxTotalReferrals: data.max_total_referrals,
  referralExpirationDays: data.referral_expiration_days,
  requirePaymentToReward: data.require_payment_to_reward,
  minimumPlanForReward: data.minimum_plan_for_reward,
  customShareMessage: data.custom_share_message,
  customLandingMessage: data.custom_landing_message,
  totalReferrals: data.total_referrals,
  successfulConversions: data.successful_conversions,
  totalRewardsGiven: data.total_rewards_given,
  createdAt: data.created_at,
  updatedAt: data.updated_at
});

const mapSettingsToDb = (settings: Partial<ReferralProgramSettings> & { organizationId: string }): any => ({
  organization_id: settings.organizationId,
  is_enabled: settings.isEnabled ?? true,
  referrer_reward_type: settings.referrerRewardType ?? 'free_month',
  referrer_reward_value: settings.referrerRewardValue ?? 1,
  referred_reward_type: settings.referredRewardType,
  referred_reward_value: settings.referredRewardValue,
  max_referrals_per_month: settings.maxReferralsPerMonth,
  max_total_referrals: settings.maxTotalReferrals,
  referral_expiration_days: settings.referralExpirationDays ?? 30,
  require_payment_to_reward: settings.requirePaymentToReward ?? true,
  minimum_plan_for_reward: settings.minimumPlanForReward,
  custom_share_message: settings.customShareMessage,
  custom_landing_message: settings.customLandingMessage,
  updated_at: new Date().toISOString()
});
